import { RestartAltRounded } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Stack,
  Typography,
} from '@mui/material';
import { useAsyncAction, useRandomPassword, useStateRef } from 'app/services';
import { userUpdatePassword } from 'app/store/user';
import React, { useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

export default function UpdatePassword({ selectedItem }) {
  const { password, generateRandomPassword } = useRandomPassword();

  const {
    asyncAction: updatePassSubmit,
    isLoading: isUpdatePassLoading,
    isSuccessful: isUpdatePassSuccessful,
    isError: isUpdatePassError,
  } = useAsyncAction(userUpdatePassword, [selectedItem]);

  const [open, setOpen] = useState(false);
  const { reset, register, formState, handleSubmit, getValues, setValue, setFocus } = useForm({
    defaultValues: {
      password: '',
    },
  });
  const [firstUpdate, setFirstUpdate, firstUpdateRef] = useStateRef(false);
  useLayoutEffect(() => {
    if (firstUpdateRef.current !== undefined) {
      setTimeout(() => setFirstUpdate(undefined));
    }
  }, [firstUpdateRef.current]);
  const handleSwitchOpenPopup = () => {
    if (!/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{6,255}$/.test(getValues().password)) {
      return;
    }
    setOpen(!open);
  };
  const handleUpdatePassword = (password) => {
    updatePassSubmit(selectedItem.id, password);
    handleSwitchOpenPopup();
    reset({
      password: '',
    });
  };
  const ref = useRef(null);
  useImperativeHandle(ref, () => ({
    handleFormSubmit: () => {
      handleSubmit((data) =>
        handleUpdatePassword({
          password: data.password.length ? data.password : undefined,
        })
      )();
    },
  }));

  const handleFormClick = () => {
    ref.current?.handleFormSubmit();
  };
  useEffect(() => {
    reset({
      password: '',
    });
  }, [selectedItem]);
  useEffect(() => {
    if (newPass) {
      setValue('password', password, {
        shouldDirty: true,
        shouldValidate: true,
        shouldTouch: true,
      });
      setNewPass(false);
    }
  }, [password]);
  const [newPass, setNewPass] = useState(false);
  const handleGenerateRandomPassword = () => {
    setFocus('password');
    generateRandomPassword();
    setNewPass(true);
  };
  return (
    <form ref={ref}>
      <Grid container>
        <Stack
          direction='column'
          alignItems='center'
          sx={(theme) => ({
            width: '100%',
            position: 'relative',
            marginTop: theme.spacing(3),
            padding: theme.spacing(2),
            border: '1px solid',
            borderRadius: theme.shape.borderRadius,
            borderColor: theme.palette.divider,
            bgcolor: 'background.paper',
            '.MuiAlert-root': {
              marginTop: theme.spacing(0),
              marginBottom: theme.spacing(3),
            },
          })}>
          <Stack
            direction='column'
            sx={(theme) => ({
              width: '100%',
              paddingBottom: theme.spacing(3),
              '.MuiTypography-h6': {
                fontSize: '15px',
              },
              '.MuiTypography-caption': {
                color: theme.palette.text.secondary,
              },
            })}>
            <Typography variant='h6'>Обновить пароль для пользователя</Typography>
            <Typography variant='caption'>
              Можно удалить данного пользователя, но это действие необратимо. Пожалуйста, будьте уверены, что Вы хотите это сделать.
            </Typography>
          </Stack>
          <Stack
            direction='column'
            justifyContent='flex-end'
            sx={(theme) => ({
              width: '100%',
              '.MuiAlert-root': {
                width: '100%',
                marginTop: theme.spacing(1),
                borderRadius: theme.shape.borderRadius,
              },
            })}>
            <FormControl error={!!formState.errors.password} margin='normal' variant='outlined' fullWidth>
              <InputLabel disableAnimation={firstUpdate}>Пароль</InputLabel>
              <OutlinedInput
                label='Пароль'
                variant='outlined'
                notched={firstUpdate}
                fullWidth
                placeholder='Введите пароль'
                endAdornment={
                  <InputAdornment position='end'>
                    <IconButton onClick={handleGenerateRandomPassword}>
                      <RestartAltRounded fontSize='small' />
                    </IconButton>
                  </InputAdornment>
                }
                {...register('password', {
                  required: false,
                  pattern: /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]).{6,255}$/,
                })}></OutlinedInput>
              <FormHelperText>
                Используйте сгенерированный пароль или задайте его самостоятельно. Помните, что для входа в приложение пользователю будет
                необходимо указать данный пароль.
              </FormHelperText>
              {!isUpdatePassLoading && isUpdatePassError && (
                <Alert severity='error'>
                  Произошла ошибка при обновлении данных. Попробуйте снова или обратитесь в техническую поддержку.
                </Alert>
              )}
              {!isUpdatePassLoading && isUpdatePassSuccessful && <Alert severity='success'>Данные успешно обновлены.</Alert>}
            </FormControl>
          </Stack>
          <Stack
            direction='row'
            justifyContent='flex-end'
            sx={(theme) => ({
              width: '100%',
              paddingTop: theme.spacing(0),
            })}>
            <LoadingButton color='error' variant='contained' onClick={handleSwitchOpenPopup} loading={isUpdatePassLoading} disableElevation>
              Обновить пароль для пользователя
            </LoadingButton>
          </Stack>
        </Stack>

        <Dialog open={open} keepMounted onClose={handleSwitchOpenPopup} aria-describedby='alert-dialog-slide-description'>
          <DialogTitle>Вы уверены что хотите обновить пароль для пользователя?</DialogTitle>
          <DialogContent>
            <DialogContentText id='alert-dialog-slide-description'>
              Вы точно уверены что хотите обновить пароль для пользователя?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSwitchOpenPopup}>Отмена</Button>
            <Button onClick={handleFormClick}>Обновить</Button>
          </DialogActions>
        </Dialog>
      </Grid>
    </form>
  );
}
